import { create } from "zustand";
import { persist } from "zustand/middleware";

export enum SideMenuBehavior {
  AUTO = "auto",
  PINNED = "pinned",
}

interface DropdownState {
  isOpen: boolean;
  projectId: string | null;
  position: { x: number; y: number };
}

interface SideMenuActions {
  open: () => void;
  close: () => void;
  openPersonalSettings: (initialSection?: string) => void;
  closePersonalSettings: () => void;
  clearInitialPersonalSettingsSection: () => void;
  allowClose: () => void;
  setSelectedProjectId: (projectId: string | null) => void;
  switchBehavior: () => void;

  // Dropdown actions
  openDropdown: (projectId: string, position: { x: number; y: number }) => void;
  closeDropdown: () => void;

  setCanReopenMenu: (canReopen: boolean) => void;

  // Scroll actions
  scrollProjectsToTop: () => void;
}

interface SideMenuState {
  behavior: SideMenuBehavior;
  isOpen: boolean;
  arePersonalSettingsOpen: boolean;
  initialPersonalSettingsSection: string | null;
  canClose: boolean;
  selectedProjectId: string | null;
  timeoutRef: NodeJS.Timeout | null;
  dropdownState: DropdownState;
  canReopenMenu: boolean;
  scrollToTopTrigger: number;

  actions: SideMenuActions;
}

const INITIAL_DROPDOWN_STATE: DropdownState = {
  isOpen: false,
  projectId: null,
  position: { x: 0, y: 0 },
};

const useSideMenuStore = create<SideMenuState>()(
  persist(
    (set, get) => ({
      behavior: SideMenuBehavior.AUTO,
      isOpen: false,
      arePersonalSettingsOpen: false,
      initialPersonalSettingsSection: null,
      canClose: true,
      selectedProjectId: null,
      timeoutRef: null,
      dropdownState: INITIAL_DROPDOWN_STATE,
      canReopenMenu: true,
      scrollToTopTrigger: 0,

      actions: {
        open: () => set({ isOpen: true }),

        close: () => {
          if (!get().canClose) return;
          set({
            isOpen: false,
            arePersonalSettingsOpen: false,
            dropdownState: INITIAL_DROPDOWN_STATE,
          });
        },

        openPersonalSettings: (initialSection?: string) => {
          set({
            arePersonalSettingsOpen: true,
            initialPersonalSettingsSection: initialSection ?? null,
          });
        },

        closePersonalSettings: () => {
          set({
            canClose: false,
            arePersonalSettingsOpen: false,
            initialPersonalSettingsSection: null,
          });
        },

        clearInitialPersonalSettingsSection: () => {
          set({ initialPersonalSettingsSection: null });
        },

        allowClose: () => set({ canClose: true }),

        setSelectedProjectId: (projectId: string | null) => {
          set({ selectedProjectId: projectId });
        },

        switchBehavior: () => {
          set((state) => ({
            behavior:
              state.behavior === SideMenuBehavior.PINNED
                ? SideMenuBehavior.AUTO
                : SideMenuBehavior.PINNED,
          }));
        },

        openDropdown: (projectId: string, position: { x: number; y: number }) => {
          set({ dropdownState: { isOpen: true, projectId, position } });
        },

        closeDropdown: () => set({ dropdownState: INITIAL_DROPDOWN_STATE }),

        setCanReopenMenu: (canReopen: boolean) => set({ canReopenMenu: canReopen }),

        scrollProjectsToTop: () => {
          set((state) => ({ scrollToTopTrigger: state.scrollToTopTrigger + 1 }));
        },
      },
    }),
    {
      name: "side-menu",
      partialize: (state) => ({ behavior: state.behavior }),
    }
  )
);

export function useIsSideMenuOpen(): boolean {
  return useSideMenuStore((state) => state.isOpen);
}

export function useSideMenuBehavior(): SideMenuBehavior {
  return useSideMenuStore((state) => state.behavior);
}

export function useArePersonalSettingsOpen(): boolean {
  return useSideMenuStore((state) => state.arePersonalSettingsOpen);
}

export function useInitialPersonalSettingsSection(): string | null {
  return useSideMenuStore((state) => state.initialPersonalSettingsSection);
}

export function useCanReopenSideMenu(): boolean {
  return useSideMenuStore((state) => state.canReopenMenu);
}

export function useSideMenuSelectedProjectId(): string | null {
  return useSideMenuStore((state) => state.selectedProjectId);
}

export function useSideMenuDropdownState(): DropdownState {
  return useSideMenuStore((state) => state.dropdownState);
}

export function useSideMenuActions(): SideMenuActions {
  return useSideMenuStore((state) => state.actions);
}

export function useScrollToTopTrigger(): number {
  return useSideMenuStore((state) => state.scrollToTopTrigger);
}
